import { useEffect, useState } from 'react';
import { config } from '../../config';
import { loadBreakCardState } from '../../app/coach';
import type { BreakCardState } from '../../app/coach';
import { log } from '../../app/logger';
import { countUnresolvedFlags } from '../../app/triage';
import { useHalftime } from '../../ui/halftime/useHalftime';
import { AddPatternSheet } from '../setup/AddPatternSheet';
import { AdjustmentsWorkflowB } from './AdjustmentsWorkflowB';
import { breakEntryCopyB, offersAdjustmentsWorkflowB, positionedRowsB, swapPlanB } from './breakHubB';
import { ARTIFACT_PHASE } from '../../types';
import type { ManageRow } from '../../app/halftime';
import type { MatchRow } from '../../types';

/**
 * B-14 — **the halftime break hub** (design spec §2.4 break-hub arm; §10 F-2/F-3; scenarios
 * 16, 20, 21): presented from `Match.state = halftime`, never navigated to. The transition
 * control (B-8) owns entering and leaving the break — the hub renders BETWEEN those
 * transitions, and the Second-half whistle stays live in the top band throughout; the hub never
 * invents its own exit.
 *
 * ── The F-2 fork, rendered ──────────────────────────────────────────────────
 *
 * A **team match** gets the adjustments-workflow entry card — optional; skipping costs nothing
 * (scenario 21: not tapping the entry writes nothing at all). An **advance scout** gets break
 * utilities only — no workflow entry anywhere (scenario 20's negative). The break utilities are
 * common to both purposes: flags triage entry (B-13's surface, its second entry point), the
 * halftime note, and board manage.
 *
 * Since B-15 the entry opens the real workflow (`AdjustmentsWorkflowB`), which follows §2.5's
 * *persistence first* rule: **opening it creates the draft row** for (match, `halftime`), and
 * the draft is resolved by finalizing it or by the workflow's one-tap Discard. That is the
 * design's own trade — the durable row is what makes a kill mid-workflow cost nothing.
 *
 * ── Nothing here decides anything ───────────────────────────────────────────
 *
 * The controller is A's own `useHalftime` hook, imported whole (the B-13 `useFlags` precedent —
 * a mirrored B controller of the note-save/voice/board-manage logic would be exactly the
 * near-duplicate the reuse contract's detector exists to catch). B renders only the hook's
 * note + manage + add halves. Its talk-point half (`togglePromotion`, the adjustment snapshot,
 * `commitHalftimeAdjustments`) is **never invoked and never rendered**: B's halftime workflow
 * is the NEW coachCards path (B-15/B-16), not a re-skin of A's — no talk-point write can leave
 * this surface because no control here dispatches one.
 *
 * ── F-3 — the engine's windows, surfaced as-is ──────────────────────────────
 *
 * The note is writable at halftime exactly (`WriteMatchNote` window; `halftime.ts:269`) and
 * board swaps happen at the break only (`SwapBoardTile`: `ERR_NOT_AT_HALFTIME`) — both surface
 * IN this hub because this hub only exists at halftime; neither window is widened or narrowed.
 * A match still in discovery at the break shows no swap controls (the board is not locked —
 * `ERR_BOARD_NOT_LOCKED` is the engine's answer, and a control that exists only to be refused
 * is a mistap manufactured on chrome); adding a pattern works in **both** capture phases
 * (`AddHypothesisAtHalftime` — state `halftime`, both phases, list-only by construction).
 *
 * ── What the hub deliberately does not do ───────────────────────────────────
 *
 * No wake-lock or recorder calls — halftime is in-match and CaptureChromeB already hosts the
 * two state-bound globals (§2.2); re-acquiring here would fight them. No undo-row publishing —
 * the shell's own reload supplies the pill's candidates at the break (the TakeoverShell
 * contract), and `refreshEpoch` flows the other way: an undo committed from the pill re-reads
 * this hub, so the manage list's counts follow. No renaming — the spec's board manage is swap +
 * add, and the engine has no hypothesis-text-edit command.
 */
export interface BreakHubSurfaceBProps {
  /** The SHELL's authoritative row — re-read after every committed transition (B-10's rule). */
  readonly match: MatchRow;
  /** B-11 — an undo committed from the pill; the hub re-reads (counts moved). */
  readonly refreshEpoch: number;
  /** Flags triage entry — the shell shows B-13's surface as the middle band (second entry point). */
  readonly onFlagsReview: () => void;
  /**
   * B-31/§21.2 — B-15's workflow, rendered INSTEAD of the hub (never over it — the hub is a band).
   *
   * The flag is the SHELL's since B-31, and that is the whole of the break fix: this component is
   * what flags review and the lock review unmount, so state held here did not survive a trip to
   * either. Owning it one level up costs a prop and makes every such trip non-destructive.
   */
  readonly workflowOpen: boolean;
  /** §21.4 — the shell finalized a recording on entry, and the workflow must say so. */
  readonly workflowNotice: string | null;
  /** The entry card's press. The shell's action: it opens the workflow AND runs §21.7's clean-ups. */
  readonly onOpenWorkflow: () => void;
  /** The workflow's own Back/Discard. A deliberate back-out is honoured; an unmount is not. */
  readonly onCloseWorkflow: () => void;
  /**
   * B-35/R-7 — the workflow reports whether the finished card is the step on screen, and the shell
   * hands it to the notification strip. Passed straight through: this hub decides nothing about it
   * and never reads it, exactly as it decides nothing about `workflowOpen`.
   */
  readonly onCardShowing: (showing: boolean) => void;
}

const MODULE = 'ui-b/capture';

export function BreakHubSurfaceB({
  match,
  refreshEpoch,
  onFlagsReview,
  workflowOpen,
  workflowNotice,
  onOpenWorkflow,
  onCloseWorkflow,
  onCardShowing,
}: BreakHubSurfaceBProps) {
  const { state, actions } = useHalftime(match.id);
  const { reload } = actions;
  const [addOpen, setAddOpen] = useState(false);
  /** The incoming row awaiting an explicit displacement choice (scenario 12, full board). */
  const [displacing, setDisplacing] = useState<ManageRow | null>(null);
  const [unresolved, setUnresolved] = useState<number | null>(null);
  /** BH-2 — what the entry card says. `none` until the read lands, which is the create copy. */
  const [cardState, setCardState] = useState<BreakCardState>(NO_CARD);

  /** An undo from the pill rewrote counts the manage list renders — re-read on the epoch. */
  useEffect(() => {
    if (refreshEpoch > 0) reload();
  }, [refreshEpoch, reload]);

  /**
   * The flags entry's count — `countUnresolvedFlags`, the same derived read the engine's own
   * triage guard uses. Re-counted on the epoch and refreshed on remount (closing the flags
   * review remounts this hub — the shell renders the review INSTEAD of the hub). Known shape,
   * documented: a flag pressed via the band while the hub is showing appears in the count on
   * the next re-read, exactly as B-13's queue behaves.
   */
  useEffect(() => {
    let live = true;
    countUnresolvedFlags(match.id).then(
      (count) => {
        if (live) setUnresolved(count);
      },
      (cause: unknown) => {
        // The count is an affordance annotation, not the queue itself — log, render no number.
        log.error(MODULE, 'B-14: the unresolved-flag count could not be read', {
          matchId: match.id,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        if (live) setUnresolved(null);
      },
    );
    return () => {
      live = false;
    };
  }, [match.id, refreshEpoch]);

  /**
   * BH-2/§21.3 — the entry card's own state. Re-read when the workflow **closes** (`workflowOpen`
   * in the deps), which is the transition that changes the answer: the user came back from having
   * built or discarded a card, and an entry card still offering to *"Build halftime adjustments"*
   * is the exact sentence that made him think his work was gone. Read on the epoch too, so an undo
   * that re-rendered the hub does not leave a stale line beside fresh counts.
   */
  useEffect(() => {
    let live = true;
    loadBreakCardState(match.id, ARTIFACT_PHASE.halftime).then(
      (next) => {
        if (live) setCardState(next);
      },
      (cause: unknown) => {
        // The entry still works; it just cannot describe itself. Falling back to the create copy
        // is the safe direction — it never claims work exists that does not.
        log.error(MODULE, 'B-31: the break card state could not be read', {
          matchId: match.id,
          message: cause instanceof Error ? cause.message : String(cause),
        });
        if (live) setCardState(NO_CARD);
      },
    );
    return () => {
      live = false;
    };
  }, [match.id, refreshEpoch, workflowOpen]);

  const offersWorkflow = offersAdjustmentsWorkflowB(match.purpose);
  const entryCopy = breakEntryCopyB(cardState);

  if (workflowOpen) {
    // B-15's workflow takes the middle band while it is open — the top-band whistle stays live
    // above it, and the hub remounts (fresh reads) when the workflow closes.
    return (
      <div
        className="bhub bhub--host"
        data-testid="bhub"
        data-view="workflow"
        data-purpose={match.purpose ?? 'unset'}
      >
        <AdjustmentsWorkflowB
          match={match}
          context={ARTIFACT_PHASE.halftime}
          entryNotice={workflowNotice}
          onCardShowing={onCardShowing}
          onClose={() => {
            onCloseWorkflow();
            reload();
          }}
        />
      </div>
    );
  }

  const view = displacing === null ? 'hub' : 'displace';
  const positionedCount = state.manageRows.filter((row) => row.boardPosition !== null).length;

  return (
    <div
      className="bhub"
      data-testid="bhub"
      data-view={view}
      data-purpose={match.purpose ?? 'unset'}
      data-phase={state.phase}
      data-note={state.note !== undefined ? 'saved' : 'none'}
      data-board={state.stillInDiscovery ? 'discovery' : 'locked'}
      role="region"
      aria-label="Half time"
    >
      {state.notice !== null && (
        <p className="bhub-notice" role="status" data-testid="bhub-notice">
          {state.notice}
          <button type="button" className="bhub-linkish" onClick={actions.dismissNotice}>
            Dismiss
          </button>
        </p>
      )}

      {state.phase === 'loading' && <p className="bhub-empty">Opening the break…</p>}
      {(state.phase === 'failed' || state.phase === 'missing') && (
        <p className="bhub-empty" role="alert" data-testid="bhub-failed">
          The break could not be loaded. The log has the detail.
        </p>
      )}

      {state.phase === 'ready' && view === 'displace' && displacing !== null && (
        <DisplacePaneB
          incoming={displacing}
          rows={state.manageRows}
          busy={state.busy}
          onChoose={(outgoingId) => {
            void actions.putOnBoard(displacing.id, outgoingId).then((done) => {
              if (done) setDisplacing(null);
            });
          }}
          onBack={() => {
            setDisplacing(null);
          }}
        />
      )}

      {state.phase === 'ready' && view === 'hub' && (
        <>
          {/* ── 1. The F-2 fork ─────────────────────────────────────────── */}
          {offersWorkflow ? (
            /* BH-2/§21.3 — the entry NAMES ITS STATE. It was pure create-copy until B-31, so a
               user returning to the hub with a finished card was told to go build one. */
            <button
              type="button"
              className="bhub-workflow"
              data-testid="bhub-workflow-entry"
              data-card={cardState.status}
              onClick={onOpenWorkflow}
            >
              <span className="bhub-workflow__title">{entryCopy.title}</span>
              <span className="bhub-workflow__note">{entryCopy.note}</span>
            </button>
          ) : (
            <p className="bhub-scout" data-testid="bhub-observation-only">
              <strong>Advance scout</strong> — halftime is just the break. Keep observing in the
              second half; points of emphasis are built at full time.
            </p>
          )}

          {/* ── 2. Flags triage entry (B-13's surface, second entry point) ─ */}
          <button
            type="button"
            className="bhub-flags"
            data-testid="bhub-flags-entry"
            onClick={onFlagsReview}
          >
            <span className="bhub-flags__title">Review flags</span>
            {unresolved !== null && unresolved > 0 && (
              <span className="bhub-flags__count" data-testid="bhub-flags-count">
                {unresolved}
              </span>
            )}
          </button>

          {/* ── 3. The halftime note (HT-8; F-3 — writable at halftime exactly) ─ */}
          <section className="bhub-card" data-testid="bhub-note-card">
            <h3 className="bhub-card__title">
              <label htmlFor="bhub-note-text">Halftime note</label>
            </h3>
            <textarea
              id="bhub-note-text"
              className="bhub-note__field"
              data-testid="bhub-note-text"
              rows={3}
              placeholder="Anything about the half worth keeping — typed or dictated."
              value={state.noteDraft ?? state.note?.text ?? ''}
              onChange={(event) => {
                actions.setNoteDraft(event.target.value);
              }}
            />
            <div className="bhub-note__actions">
              <button
                type="button"
                className="bhub-primary"
                data-testid="bhub-note-save"
                disabled={state.noteDraft === null || state.busy}
                onClick={() => {
                  void actions.saveNote();
                }}
              >
                Save note
              </button>
              <button
                type="button"
                className="bhub-secondary"
                data-testid="bhub-note-voice"
                data-recording={state.noteRecording}
                onClick={() => {
                  void actions.toggleNoteRecording();
                }}
              >
                {state.noteRecording ? 'Stop recording' : 'Record instead'}
              </button>
            </div>
            {state.note !== undefined && (
              <p className="bhub-note__status" data-testid="bhub-note-status" role="status">
                Saved — it stays with the match.
              </p>
            )}
            {/* §8.2's honesty: only the stored row can say what the audio is. */}
            {state.noteAudio !== null && (
              <p className="bhub-note__audio" data-testid="bhub-note-audio">
                {state.noteAudio.available
                  ? 'Voice note saved.'
                  : (state.noteAudio.caption ?? 'audio unavailable')}
              </p>
            )}
          </section>

          {/* ── 4. Board manage (F-3 — swaps at the break only; add, both phases) ─ */}
          <section className="bhub-card" data-testid="bhub-manage">
            <div className="bhub-card__bar">
              <h3 className="bhub-card__title">
                {state.stillInDiscovery ? 'Patterns' : 'Manage the board'}
              </h3>
              <button
                type="button"
                className="bhub-secondary"
                data-testid="bhub-add-pattern"
                onClick={() => {
                  setAddOpen(true);
                }}
              >
                Add a pattern
              </button>
            </div>
            {!state.stillInDiscovery && (
              <p className="bhub-card__note">
                {positionedCount >= config.BOARD_CAP
                  ? 'The board is full — putting a pattern on it means choosing what comes off.'
                  : 'The board has room — a pattern goes straight on.'}
              </p>
            )}
            <ul className="bhub-rows" data-testid="bhub-rows">
              {state.manageRows.map((row) => (
                <li
                  className="bhub-row"
                  key={row.id}
                  data-testid={`bhub-row-${row.id}`}
                  data-status={row.status}
                >
                  <span className="bhub-row__count">{row.cachedCount}</span>
                  <span className="bhub-row__text">{row.text}</span>
                  {row.boardPosition !== null ? (
                    <span className="bhub-row__badge" data-testid={`bhub-onboard-${row.id}`}>
                      On the board
                    </span>
                  ) : (
                    !state.stillInDiscovery && (
                      <button
                        type="button"
                        className="bhub-row__toboard"
                        data-testid={`bhub-toboard-${row.id}`}
                        disabled={state.busy}
                        onClick={() => {
                          const plan = swapPlanB(state.manageRows, config.BOARD_CAP);
                          if (plan.kind === 'fill') void actions.putOnBoard(row.id, null);
                          else setDisplacing(row);
                        }}
                      >
                        To board
                      </button>
                    )
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      {/* B-7's sheet, reused whole (text + the seven-phase picker) — the B-9 precedent.
          Typing is legal here: halftime is a natural pause point (PRE-3 permits free text at
          Pre-Match and Halftime, and nowhere else). */}
      {addOpen && (
        <AddPatternSheet
          onAdd={(text, phase) => {
            void actions.addFreeText(text, phase).then((done) => {
              if (done) setAddOpen(false);
            });
          }}
          onClose={() => {
            setAddOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** The pre-read default, and the honest answer when the read fails: no card, no counts. */
const NO_CARD: BreakCardState = { status: 'none', accepted: 0, renderable: 0 };

// ── The displacement picker (scenario 12: explicit, never silent) ─────────────

interface DisplacePaneBProps {
  readonly incoming: ManageRow;
  readonly rows: readonly ManageRow[];
  readonly busy: boolean;
  readonly onChoose: (outgoingId: string) => void;
  readonly onBack: () => void;
}

/**
 * An inline pane, deliberately NOT a dialog (the B-13 promote-pane shape): the whistle in the
 * top band stays live — the break ends when the game says so, and a modal would inert the one
 * control that must never be fenced. The outgoing list is re-derived from the same rows the
 * manage list renders, position order; the engine's handler stays the authority on the swap
 * itself.
 */
function DisplacePaneB({ incoming, rows, busy, onChoose, onBack }: DisplacePaneBProps) {
  const positioned = positionedRowsB(rows);
  return (
    <div className="bhub-displace" data-testid="bhub-displace">
      <div className="bhub-card__bar">
        <h3 className="bhub-card__title">What does it replace?</h3>
        <button type="button" className="bhub-secondary" data-testid="bhub-displace-back" onClick={onBack}>
          Back
        </button>
      </div>
      <p className="bhub-card__note">
        <strong>{incoming.text}</strong> goes on the board in the tile you pick.
      </p>
      <ul className="bhub-rows">
        {positioned.map((row) => (
          <li className="bhub-row" key={row.id} data-status={row.status}>
            <span className="bhub-row__count">{row.cachedCount}</span>
            <span className="bhub-row__text">{row.text}</span>
            <button
              type="button"
              className="bhub-row__toboard"
              data-testid={`bhub-displace-${row.id}`}
              disabled={busy}
              onClick={() => {
                onChoose(row.id);
              }}
            >
              Replace
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
